/**
 * Vision API 서비스
 *
 * 사진 채점 모드의 API 호출 처리
 * - gradePage: 이미지 → Vision API → 인식 결과
 * - gradePageWithRetry: 재시도 로직이 포함된 gradePage
 * - confirmGrading: 수정된 답안 → DB 저장 → 최종 채점 결과
 */

import axiosInstance from '../config/axios';
import {
  VisionGradeResponse,
  VisionConfirmRequest,
  VisionConfirmResponse,
  RetryCallbackParams,
  VisionProcessingStage,
} from '../types/vision';
import {
  classifyError,
  shouldRetry,
  calculateRetryDelay,
  delay,
  logVisionError,
  DEFAULT_RETRY_CONFIG,
} from '../utils/visionErrorHandler';

/**
 * 페이지 이미지 채점 요청
 *
 * 답안지 이미지를 Vision API로 전송하여 답안을 인식하고
 * 사전 채점 결과를 반환받음 (확정 전)
 *
 * @param image - 답안지 이미지 파일 (File 또는 Blob)
 * @param workbookName - 문제집 이름
 * @param page - 페이지 번호
 * @returns Vision 채점 응답 (확정 전 상태)
 *
 * @example
 * ```typescript
 * const response = await gradePage(imageFile, '수학익힘책', 15);
 * console.log(response.recognized_answers); // 인식된 답안 목록
 * console.log(response.overall_confidence); // 전체 신뢰도
 * ```
 */
export const gradePage = async (
  image: File | Blob,
  workbookName: string,
  page: number
): Promise<VisionGradeResponse> => {
  // FormData 생성 (multipart/form-data)
  const formData = new FormData();

  // 이미지 파일 추가 (파일명 지정)
  formData.append('image', image, 'answer_sheet.jpg');

  // 문제집 정보 추가
  formData.append('workbook_name', workbookName);
  formData.append('page', page.toString());

  // API 호출 (30초 타임아웃 - Vision API 처리 시간 고려)
  const response = await axiosInstance.post<VisionGradeResponse>(
    '/vision/grade-page',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 30000, // 30초
    }
  );

  return response.data;
};

/**
 * 채점 결과 확정 요청
 *
 * 학생이 인식 결과를 확인/수정한 후 최종 확정
 * DB에 submissions 레코드가 생성됨
 *
 * @param request - 확정 요청 데이터 (student_id, workbook_name, page, answers)
 * @returns 최종 채점 결과
 *
 * @example
 * ```typescript
 * const response = await confirmGrading({
 *   student_id: 'uuid-string',
 *   workbook_name: '수학익힘책',
 *   page: 15,
 *   answers: [
 *     { problem_number: '1', final_answer: '3' },
 *     { problem_number: '2', final_answer: 'ㄱ,ㄴ' },
 *   ]
 * });
 * console.log(response.correct_count); // 정답 개수
 * console.log(response.total_score);   // 총점
 * ```
 */
export const confirmGrading = async (
  request: VisionConfirmRequest
): Promise<VisionConfirmResponse> => {
  const response = await axiosInstance.post<VisionConfirmResponse>(
    '/vision/confirm',
    request
  );

  return response.data;
};

/**
 * 진행 상태 콜백 옵션
 */
export interface GradePageOptions {
  /** 처리 단계 변경 시 호출되는 콜백 */
  onStageChange?: (stage: VisionProcessingStage) => void;

  /** 재시도 발생 시 호출되는 콜백 */
  onRetry?: (params: RetryCallbackParams) => void;

  /** AbortController signal (취소용) */
  signal?: AbortSignal;
}

/**
 * 재시도 로직이 포함된 페이지 이미지 채점 요청
 *
 * 네트워크 에러, 타임아웃, 서버 에러 발생 시 자동으로 재시도합니다.
 * 지수 백오프를 사용하여 재시도 간격을 점진적으로 늘립니다.
 *
 * @param image - 답안지 이미지 파일
 * @param workbookName - 문제집 이름
 * @param page - 페이지 번호
 * @param options - 진행 상태 콜백 옵션
 * @returns Vision 채점 응답
 * @throws VisionError - 재시도 후에도 실패한 경우
 *
 * @example
 * ```typescript
 * const response = await gradePageWithRetry(
 *   imageFile,
 *   '수학익힘책',
 *   15,
 *   {
 *     onStageChange: (stage) => setProcessingStage(stage),
 *     onRetry: ({ attempt, delay }) => {
 *       console.log(`재시도 ${attempt}회 (${delay}ms 후)`);
 *     },
 *   }
 * );
 * ```
 */
export const gradePageWithRetry = async (
  image: File | Blob,
  workbookName: string,
  page: number,
  options?: GradePageOptions
): Promise<VisionGradeResponse> => {
  const { onStageChange, onRetry, signal } = options || {};

  let attemptCount = 0;
  let lastError: unknown = null;

  while (attemptCount < DEFAULT_RETRY_CONFIG.maxAttempts) {
    attemptCount++;

    // 취소 확인
    if (signal?.aborted) {
      throw new Error('요청이 취소되었습니다');
    }

    try {
      // 업로드 단계 알림
      onStageChange?.('uploading');

      // FormData 생성
      const formData = new FormData();
      formData.append('image', image, 'answer_sheet.jpg');
      formData.append('workbook_name', workbookName);
      formData.append('page', page.toString());

      // 분석 단계 알림 (요청 시작)
      onStageChange?.('analyzing');

      // API 호출
      const response = await axiosInstance.post<VisionGradeResponse>(
        '/vision/grade-page',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 30000, // 30초
          signal, // AbortController signal 전달
        }
      );

      // 채점 완료 단계 알림
      onStageChange?.('grading');

      return response.data;
    } catch (error) {
      lastError = error;

      // 취소된 경우 즉시 throw
      if (signal?.aborted) {
        throw new Error('요청이 취소되었습니다');
      }

      // 에러 분류
      const visionError = classifyError(error);
      logVisionError(visionError, { attemptCount, workbookName, page });

      // 재시도 가능 여부 확인
      if (shouldRetry(visionError, attemptCount)) {
        const retryDelay = calculateRetryDelay(attemptCount);

        // 재시도 콜백 호출
        onRetry?.({
          attempt: attemptCount,
          delay: retryDelay,
          error: visionError,
        });

        // 대기 후 재시도
        await delay(retryDelay);
        continue;
      }

      // 재시도 불가능한 에러는 VisionError로 throw
      throw visionError;
    }
  }

  // 최대 재시도 횟수 초과
  const finalError = classifyError(lastError);
  logVisionError(finalError, {
    attemptCount,
    workbookName,
    page,
    message: '최대 재시도 횟수 초과',
  });

  throw finalError;
};
